import { action }                 from 'mobx';
import { computed }               from 'mobx';
import { makeObservable }         from 'mobx';
import { observable }             from 'mobx';
import { reaction }               from 'mobx';
import { runInAction }            from 'mobx';
import { pointInPolygon }         from '@tokamap/core';
import { interiorPoint }          from '@tokamap/core';
import { mergeBasemapBuildings }  from '@tokamap/core';
import { isSameBasemapBuilding }  from '@tokamap/core';
import type { BasemapBuilding }   from '@tokamap/core';
import type { LngLat }            from '@tokamap/core';
import type { MapAdapterEvent }   from '@tokamap/core';
import type { Shape }             from '@tokamap/core';
import { message }                from '@widesk-ui/hooks/useMessage';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import asSingleHistoryEntry       from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import { buildingAtPoint }        from '@/TokamapEditor/helpers/planBuildings';
import { createBuildingShapes }   from '@/TokamapEditor/helpers/planBuildings';
import { isBuildingPreviewShapeId } from '@/TokamapEditor/helpers/planBuildings';
import { previewBuildings }       from '@/TokamapEditor/helpers/planBuildings';
import { removeBuildingsPreview } from '@/TokamapEditor/helpers/planBuildings';
import { isBackgroundShapeId }    from '@/TokamapEditor/helpers/planBackground';
import { MAX_SELECTION_SHAPES }   from '@/TokamapEditor/ux/constants';

const ACTIVE_CLASS = 'building-pick-active';

/**
 * La baguette : reprendre des bâtiments du fond OSM en formes de la carte.
 *
 * Rien n'est créé avant « Créer » : les bâtiments retenus ne sont qu'un aperçu, qu'on corrige au
 * clic ou par zone (rectangle au Ctrl + glisser, lasso au Maj + glisser) — une zone qui touche des
 * bâtiments retenus les retire, sinon elle ajoute les siens. La détection n'est pas parfaite
 * (bâtiments coupés par les tuiles, absents d'OSM) : c'est sur l'aperçu qu'on le voit.
 */
export class BuildingPickStore {
	active = false;
	buildings: BasemapBuilding[] = [];
	/** Pendant le tracé d'une zone : le nombre de bâtiments retenus qu'elle donnerait au relâchement. */
	zoneResultCount: number | null = null;

	private zoning = false;
	private hovered: BasemapBuilding | null = null;
	/** Dernier bâtiment trouvé sous la souris, couvert ou non : on ne réexamine les formes qu'en en changeant. */
	private probed: BasemapBuilding | null = null;
	private zoneShapes: ShapeBox[] | null = null;
	private zoneCorners: LngLat[] | undefined;
	private zoneFrame = 0;
	private teardown: (() => void)[] = [];

	constructor() {
		makeObservable(this, {
			active: observable,
			buildings: observable.ref,
			zoneResultCount: observable,
			count: computed,
			start: action,
			stop: action,
		});

		reaction(() => currentEditorStore.get, () => this.stop());
	}

	get count() {
		return this.buildings.length;
	}

	/** La carte est sur le fond OSM : la baguette a quelque chose à lire. */
	isAvailable() {
		return currentEditorStore.get?.mapAdapter.getTileLayerName() === 'osm';
	}

	/** Le fond montre ses bâtiments au zoom courant. */
	canPick() {
		return !!currentEditorStore.get?.mapAdapter.basemapBuildingsVisible();
	}

	toggle() {
		if (this.active) this.stop();
		else this.start();
	}

	start() {
		const instance = currentEditorStore.get;
		if (this.active || !instance || !this.isAvailable()) return;

		instance.mapAdapter.stopDrawingRegion();
		instance.mapAdapter.stopDrawingOpening();
		// Les formes déjà posées ne réagissent plus : la baguette vise ce qu'il y a dessous.
		instance.mapAdapter.setShapesInert(true);
		instance.mapAdapter.setBasemapBuildingsRevealed(true);
		this.active = true;

		const container = instance.mapAdapter.getContainerElement();
		container?.classList.add(ACTIVE_CLASS);

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') this.stop();

			const target = event.target as HTMLElement | null;
			const typing = !!target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);
			if (event.key === 'Enter' && !typing && this.count) {
				event.preventDefault();
				void this.create();
			}
		};

		// En capture, sur le conteneur : l'outil zone doit être armé AVANT que la carte ne traite ce
		// même clic — sans quoi elle le prendrait pour un déplacement, ou Ctrl pour une sélection.
		const onPointerDown = (event: MouseEvent) => {
			if (event.button !== 0) return;

			const lasso = event.shiftKey;
			if (!lasso && !event.ctrlKey && !event.metaKey) return;

			this.zoning = true;
			instance.mapAdapter.startDrawingRegion({ followCamera: true, lasso });
		};

		const onPointerLeave = () => {
			this.probed = null;
			this.setHovered(null);
		};

		window.addEventListener('keydown', onKeyDown);
		container?.addEventListener('mousedown', onPointerDown, { capture: true });
		container?.addEventListener('mouseleave', onPointerLeave);

		const unsubscribe = instance.mapAdapter.on(event => this.handleMapEvent(event));

		this.teardown = [
			unsubscribe,
			() => window.removeEventListener('keydown', onKeyDown),
			() => container?.removeEventListener('mousedown', onPointerDown, { capture: true }),
			() => container?.removeEventListener('mouseleave', onPointerLeave),
			() => this.setHovered(null),
			() => container?.classList.remove(ACTIVE_CLASS),
			() => instance.mapAdapter.setShapesInert(false),
			() => instance.mapAdapter.setBasemapBuildingsRevealed(false),
		];
	}

	/** Range la baguette et retire l'aperçu, sans rien créer. */
	stop() {
		if (!this.active) return;

		this.teardown.forEach(fn => fn());
		this.teardown = [];
		this.endZoning();

		const instance = currentEditorStore.get;
		if (instance) removeBuildingsPreview(instance);

		this.active = false;
		this.buildings = [];
	}

	async create() {
		const instance = currentEditorStore.get;
		if (!instance || !this.buildings.length) return;

		const buildings = this.buildings;
		const ids = await asSingleHistoryEntry(() => createBuildingShapes(instance, buildings));

		this.stop();
		instance.mapAdapter.selectShapes(ids);
		message.success(`${ids.length} bâtiment${ids.length > 1 ? 's' : ''} créé${ids.length > 1 ? 's' : ''}.`);
	}

	private handleMapEvent(event: MapAdapterEvent) {
		switch (event.type) {
			case 'map:click':
				this.pickAt(event.position);
				break;

			case 'map:pointer-moved':
				if (!this.zoning) this.hoverAt(event.position);
				break;

			case 'region:changed':
				if (!this.zoning) break;
				if (event.interacting) {
					this.scheduleHoverZone(event.corners);
					break;
				}
				this.endZoning();
				if (!event.region) break;
				if (event.corners) this.toggleZone(event.corners);
				currentEditorStore.get?.mapAdapter.setRegion(null);
				break;

			// Un autre outil prend la main : la baguette s'efface plutôt que de rester armée dessous.
			case 'drawing:started':
				this.stop();
				break;
		}
	}

	/** Un bâtiment déjà couvert par une forme n'est plus à prendre : il n'est pas surligné. */
	private hoverAt(position: LngLat) {
		const building = currentEditorStore.get?.mapAdapter.basemapBuildingAt(position) ?? null;
		const unchanged = building && this.probed ? isSameBasemapBuilding(building, this.probed) : building === this.probed;
		if (unchanged) return;

		this.probed = building;
		this.setHovered(building && !existingShapeOver(building, shapeBoxes(mapShapes())) ? building : null);
	}

	/** Ne redessine que si l'on passe à un autre bâtiment : la souris en survole un même longtemps. */
	private setHovered(building: BasemapBuilding | null) {
		const unchanged = building && this.hovered
			? isSameBasemapBuilding(building, this.hovered)
			: building === this.hovered;
		if (unchanged) return;

		this.hovered = building;
		currentEditorStore.get?.mapAdapter.setBasemapBuildingHover(building ? [building] : []);
	}

	private scheduleHoverZone(corners: LngLat[] | undefined) {
		this.zoneCorners = corners;
		if (this.zoneFrame) return;

		this.zoneFrame = requestAnimationFrame(() => {
			this.zoneFrame = 0;
			if (this.zoning) this.hoverZone(this.zoneCorners);
		});
	}

	/**
	 * Pendant le tracé d'une zone : ce qu'elle va prendre, lu sur la carte affichée pour suivre la
	 * souris — en rouge si le résultat dépasserait le maximum.
	 */
	private hoverZone(corners: LngLat[] | undefined) {
		const adapter = currentEditorStore.get?.mapAdapter;
		if (!adapter) return;

		const inZone = corners ? this.newBuildingsIn(corners) : [];
		const resultCount = inZone.length ? this.toggled(inZone).length : null;
		const overLimit = (resultCount ?? 0) > MAX_SELECTION_SHAPES;

		this.hovered = null;
		this.probed = null;
		adapter.setBasemapBuildingHover(inZone, { overLimit });
		adapter.setRegionOverLimit(overLimit);
		runInAction(() => this.zoneResultCount = resultCount);
	}

	private endZoning() {
		if (!this.zoning) return;

		this.zoning = false;
		this.zoneShapes = null;
		cancelAnimationFrame(this.zoneFrame);
		this.zoneFrame = 0;
		this.hovered = null;
		this.probed = null;
		runInAction(() => this.zoneResultCount = null);
		currentEditorStore.get?.mapAdapter.setBasemapBuildingHover([]);
		currentEditorStore.get?.mapAdapter.setRegionOverLimit(false);
		currentEditorStore.get?.mapAdapter.stopDrawingRegion();
	}

	private pickAt(position: LngLat) {
		const instance = currentEditorStore.get;
		if (!instance || this.zoning) return;

		const picked = buildingAtPoint(this.buildings, position);
		if (picked !== null) return this.setBuildings(this.buildings.filter((_, index) => index !== picked));

		const building = instance.mapAdapter.basemapBuildingAt(position);
		if (!building) return;

		if (existingShapeOver(building, shapeBoxes(mapShapes()))) return;

		const next = mergeBasemapBuildings(this.buildings, [building]);
		if (next.length > MAX_SELECTION_SHAPES) return warnOverLimit(next.length);

		this.setBuildings(next);
	}

	/**
	 * Lue sur la carte affichée, comme le survol pendant le tracé : la zone vient d'être tracée à
	 * l'écran, ses bâtiments y sont rendus. Une lecture hors écran (`queryBasemapBuildings`) coûterait
	 * plusieurs secondes de plus pour le même résultat.
	 */
	private toggleZone(corners: LngLat[]) {
		const inZone = this.newBuildingsIn(corners);
		if (!inZone.length) return void message.info('Aucun bâtiment dans cette zone.');

		const next = this.toggled(inZone);
		if (next.length > MAX_SELECTION_SHAPES) return warnOverLimit(next.length);

		this.setBuildings(next);
	}

	/** Les bâtiments du fond dans la zone qui n'ont pas déjà leur forme sur la carte. */
	private newBuildingsIn(corners: LngLat[]): BasemapBuilding[] {
		const adapter = currentEditorStore.get?.mapAdapter;
		if (!adapter) return [];

		const shapes = this.zoning
			? (this.zoneShapes ??= shapeBoxes(mapShapes()))
			: shapeBoxes(mapShapes());

		return adapter.basemapBuildingsIn(corners).filter(building => !existingShapeOver(building, shapes));
	}

	/**
	 * Une zone qui touche des bâtiments déjà retenus les retire tous, sans rien ajouter ; sinon elle
	 * ajoute les siens. Inverser chacun ferait d'une même zone un mélange imprévisible d'ajouts et de
	 * retraits.
	 */
	private toggled(inZone: BasemapBuilding[]): BasemapBuilding[] {
		const isInZone = (building: BasemapBuilding) => inZone.some(other => isSameBasemapBuilding(other, building));
		const kept = this.buildings.filter(building => !isInZone(building));

		if (kept.length < this.buildings.length) return kept;

		return mergeBasemapBuildings(this.buildings, inZone);
	}

	private setBuildings(buildings: BasemapBuilding[]) {
		const instance = currentEditorStore.get;

		runInAction(() => this.buildings = buildings);

		if (instance) previewBuildings(instance, buildings);
	}
}

/** Les formes de la carte, aperçus et échafaudages exclus — lues une fois par geste, pas par bâtiment. */
function mapShapes(): Shape[] {
	return (currentEditorStore.get?.shapeStore.getAll() ?? []).filter(shape => (
		!isBuildingPreviewShapeId(shape.id)
		&& !isBackgroundShapeId(shape.id)
		&& shape.coordinates.length > 2
	));
}

type ShapeBox = {
	shape: Shape;
	minLng: number;
	minLat: number;
	maxLng: number;
	maxLat: number;
};

function shapeBoxes(shapes: Shape[]): ShapeBox[] {
	return shapes.map(shape => {
		const box = { shape, minLng: Infinity, minLat: Infinity, maxLng: -Infinity, maxLat: -Infinity };

		for (const [lng, lat] of shape.coordinates as LngLat[]) {
			if (lng < box.minLng) box.minLng = lng;
			if (lng > box.maxLng) box.maxLng = lng;
			if (lat < box.minLat) box.minLat = lat;
			if (lat > box.maxLat) box.maxLat = lat;
		}

		return box;
	});
}

/** Forme de la carte déjà posée sur ce bâtiment : il n'est plus à reprendre. */
function existingShapeOver(building: BasemapBuilding, boxes: ShapeBox[]): Shape | undefined {
	const [lng, lat] = interiorPoint(building.ring, building.holes);

	return boxes.find(({ shape, minLng, minLat, maxLng, maxLat }) => (
		lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat
		&& pointInPolygon([lng, lat], shape.coordinates as LngLat[])
		&& !shape.holes?.some(hole => pointInPolygon([lng, lat], hole))
	))?.shape;
}

function warnOverLimit(count: number) {
	message.warning(`${count} bâtiments : la sélection est limitée à ${MAX_SELECTION_SHAPES}. Réduisez-la.`);
}

export default new BuildingPickStore();
